import { useMemo } from 'react';
import type { ProfileAddress, UserProfile } from '@/core/models/user';
import { AddressType } from '@/core/models/address-type';
import useUserProfile from '@/core/queries/use-user-profile-query';

function pickProfileAddress(
  profile: UserProfile,
  which: AddressType,
): ProfileAddress | undefined {
  return which === AddressType.Shipping
    ? (profile.shippingAddress ?? undefined)
    : (profile.billingAddress ?? undefined);
}

const useUserDefaultAddress = (
  options: {
    defaultBillingAddress?: boolean;
    defaultShippingAddress?: boolean;
  } = {},
): {
  data: ProfileAddress | undefined;
  isLoading: boolean;
} => {
  const { data: profile, isLoading } = useUserProfile();
  const { defaultBillingAddress, defaultShippingAddress } = options;

  const data = useMemo(() => {
    if (!profile) return undefined;

    if (defaultShippingAddress && defaultBillingAddress) {
      return (
        pickProfileAddress(profile, AddressType.Shipping) ??
        pickProfileAddress(profile, AddressType.Billing)
      );
    }

    if (defaultShippingAddress) {
      return pickProfileAddress(profile, AddressType.Shipping);
    }

    if (defaultBillingAddress) {
      return pickProfileAddress(profile, AddressType.Billing);
    }

    return undefined;
  }, [profile, defaultBillingAddress, defaultShippingAddress]);

  return { data, isLoading };
};

export default useUserDefaultAddress;
